import { SceneFactProposalStatus } from "@sitescene/types";
import { useState } from "react";
import { useNavigate } from "react-router";

import { Badge } from "@/components/badge";
import { type Column, DataTable } from "@/components/data-table";
import { DetailPanel } from "@/components/detail-panel";
import { Button, Field, TextArea, TextInput } from "@/components/field";
import { StatusMessage } from "@/components/status-message";
import { sceneProposalPath } from "@/constants/routes";
import {
	apiErrorMessage,
	type SceneFactProposalListItem,
	useCreateSceneProposal,
	useSceneProposals,
} from "@/features/scene-facts/use-scene-proposals";
import { formatWhen } from "@/pages/plausibility/format";
import { cx } from "@/utils/cx";
import { parseEventId, refId } from "@/utils/ids";

import { formatAge, SOURCE_LABELS, STATUS_LABELS } from "./format";

const {
	Applied,
	Drafting,
	Failed,
	NoChange,
	Pending,
	Rejected,
	Reverted,
	Superseded,
} = SceneFactProposalStatus;

interface Tab {
	label: string;
	/** Omitted for every status. */
	statuses?: readonly SceneFactProposalStatus[];
}

/**
 * Every status sits under one tab: what still needs a person (a failed or
 * empty draft needs a redraft or a rejection too), what reached a camera,
 * and what was closed without doing so.
 */
const TABS: readonly [Tab, ...Tab[]] = [
	{ label: "To review", statuses: [Drafting, Pending, Failed, NoChange] },
	{ label: "Applied", statuses: [Applied, Reverted] },
	{ label: "Rejected", statuses: [Rejected, Superseded] },
	{ label: "All" },
];

/**
 * Scene-fact proposals for the selected organization: notes about a clip,
 * from staff or from a customer's edit of an alert, that a model has turned
 * into descriptive facts about a camera's scene. Each waits here until staff
 * apply or reject it.
 */
export const SceneProposals = () => {
	const [tab, setTab] = useState<Tab>(TABS[0]);
	// Fixed at mount so rendering stays pure. Ages are coarse enough not to
	// need a ticking clock; a refetch that brings in newer rows reads "just now".
	const [now] = useState(() => Date.now());
	const navigate = useNavigate();
	const query = useSceneProposals(tab.statuses);
	const rows = query.data ?? [];

	const columns: Column<SceneFactProposalListItem>[] = [
		{
			header: "Created",
			key: "created",
			render: (row) => (
				<span title={formatWhen(row.createdAt)}>
					{formatAge(row.createdAt, now)}
				</span>
			),
		},
		{
			header: "Source",
			key: "source",
			render: (row) => (
				<Badge label={SOURCE_LABELS[row.source]} value={row.source} />
			),
		},
		{
			header: "Status",
			key: "status",
			render: (row) => (
				<Badge label={STATUS_LABELS[row.status]} value={row.status} />
			),
		},
		{
			header: "Camera",
			key: "camera",
			render: (row) =>
				row.sensorName ?? <code className="text-xs">{refId(row.sensor)}</code>,
		},
		{
			header: "Event",
			key: "event",
			render: (row) =>
				row.eventTitle ?? <span className="text-tertiary">—</span>,
		},
		{
			className: "max-w-md",
			header: "Note",
			key: "note",
			render: (row) => (
				<span className="text-secondary line-clamp-2 text-xs">{row.note}</span>
			),
		},
	];

	return (
		<div className="flex flex-col gap-4">
			<NewFromEvent />

			{/* A filter over one list, not tabbed panels: toggle buttons in a
			    labelled group, so each announces whether it is the one applied. */}
			<div
				aria-label="Proposal status"
				className="flex flex-wrap gap-1"
				role="group"
			>
				{TABS.map((t) => (
					<button
						aria-pressed={t === tab}
						className={cx(
							"rounded-md px-3 py-1 text-sm font-medium",
							t === tab
								? "bg-brand-50 text-brand-800"
								: "text-secondary hover:bg-secondary hover:text-primary",
						)}
						key={t.label}
						onClick={() => setTab(t)}
						type="button"
					>
						{t.label}
					</button>
				))}
			</div>

			<div>
				<StatusMessage
					empty="No proposals here. Draft one from a verification, or from an event ID above."
					error={query.error}
					isEmpty={!query.isLoading && rows.length === 0}
					isLoading={query.isLoading}
					loading="Loading proposals…"
				/>
				{rows.length > 0 && (
					<DataTable
						columns={columns}
						onRowClick={(row) => void navigate(sceneProposalPath(row.id))}
						rowKey={(row) => row.id}
						rows={rows}
					/>
				)}
			</div>
		</div>
	);
};

/**
 * For when a customer sends staff an event link rather than staff finding
 * the clip in Verifications. The draft reads the event's highest-severity
 * detection that has a clip.
 */
const NewFromEvent = () => {
	const navigate = useNavigate();
	const create = useCreateSceneProposal();
	const [eventInput, setEventInput] = useState("");
	const [note, setNote] = useState("");
	const eventId = parseEventId(eventInput);
	const ready = Boolean(eventId) && Boolean(note.trim());

	return (
		<DetailPanel title="New from event ID">
			<form
				className="flex flex-col gap-3"
				onSubmit={(event) => {
					event.preventDefault();
					if (!ready) return;
					create.mutate(
						{ eventId, note: note.trim() },
						{
							onSuccess: (proposal) =>
								void navigate(sceneProposalPath(proposal.id)),
						},
					);
				}}
			>
				<div className="grid gap-3 lg:grid-cols-[20rem_1fr]">
					<Field htmlFor="new-event-id" label="Event ID or link">
						<TextInput
							id="new-event-id"
							onChange={(e) => setEventInput(e.target.value)}
							placeholder="66f1c0…"
							value={eventInput}
						/>
					</Field>
					<Field
						htmlFor="new-event-note"
						label="What should this camera know about its scene?"
					>
						<TextArea
							id="new-event-note"
							onChange={(e) => setNote(e.target.value)}
							placeholder="The bottom of the frame is more store; the only door out is top right."
							rows={2}
							value={note}
						/>
					</Field>
				</div>
				<div>
					<Button disabled={create.isPending || !ready} type="submit">
						{create.isPending ? "Queuing…" : "Draft scene update"}
					</Button>
				</div>
				{create.error && (
					<p className="text-error-primary text-sm" role="alert">
						{apiErrorMessage(create.error)}
					</p>
				)}
			</form>
		</DetailPanel>
	);
};
